// How a result over several games is said on every page: the record (2 wins, 2 draws, 6 losses) and the points
// taken out of every ten games (3 out of 10, a draw is half). Decided on 2026-10-12 in place of "scores 30%" and
// "21 points under your average": a percentage of points is a unit no player counts in, and points of a
// percentage on top of it was two of them. A player knows what winning three games in ten is.
//
// Pure. Every page that says how games went goes through here, so the two can never drift apart again. In the
// page's language (i18n/common.ts): "3 puntos de 10", "2V 2T 6D", and a decimal comma in Spanish.

import { language } from "../services/i18n.ts";
import { t } from "../i18n/common.ts";

/** One decimal, written as the page's language writes it: 4.6, or 4,6 in Spanish. */
const tenth = (value: number): string => {
    const text = value.toFixed(1);
    return language() === "es" ? text.replace(".", ",") : text;
};

/** Points out of ten, from a score in percent: 30 → "3", 46 → "4.6", 55 → "5.5". One decimal at most. */
export function outOfTen(scorePct: number): string {
    const tenths = Math.round(scorePct);
    return tenths % 10 === 0 ? String(tenths / 10) : tenth(tenths / 10);
}

/** "3/10": for a cell, a badge, anywhere a sentence does not fit. */
export function pointsShort(scorePct: number): string {
    return `${outOfTen(scorePct)}/10`;
}

/** "3 points out of 10" (and "1 point out of 10"). */
export function pointsLong(scorePct: number): string {
    return t("pointsOutOfTen", outOfTen(scorePct));
}

/**
 * The difference between two scores, in points out of ten and with its sign: "−2.1", "+0.8", "0". A minus
 * sign and not a hyphen, because it is read as a number.
 */
export function pointsDelta(scorePct: number, usualPct: number): string {
    const tenths = Math.round(scorePct - usualPct);
    if (tenths === 0) return "0";
    const value = Math.abs(tenths) % 10 === 0 ? String(Math.abs(tenths) / 10) : tenth(Math.abs(tenths) / 10);
    return `${tenths < 0 ? "−" : "+"}${value}`;
}

/** Game points, counted and not out of ten, to a tenth: 4.25 → "4.3", 1 → "1". */
export function pointsValue(points: number): string {
    const tenths = Math.round(points * 10);
    return tenths % 10 === 0 ? String(tenths / 10) : tenth(tenths / 10);
}

/**
 * "4.3 points", "1 point": what an opening cost over all its games against the usual, which is the points
 * there are to gain by studying it.
 */
export function pointsCount(points: number): string {
    return t("points", pointsValue(points));
}

/** "2 wins, 2 draws, 6 losses": the record in words, every part of it, zeros included. */
export function recordText(wins: number, draws: number, losses: number): string {
    return t("record", wins, draws, losses);
}

/** "2W 2D 6L": the record where a sentence does not fit. */
export function recordShort(wins: number, draws: number, losses: number): string {
    return t("recordShort", wins, draws, losses);
}
